// cogsFormat.ts — shared COGS formatting + types for the Simple/Bundle table, the Bundle
// Categories cards and the parent detail / Version History view, so every view renders the same
// INR formatting, the same "as of <Mon YYYY>" treatment and the same post-GST labels.
//
// COGS is POST-GST: the bundle total is the sum of its children's post-GST COGS (from Redshift's
// current_inventory_cogs for live rows, or a sealed VersionCogsSnapshot for frozen/as-of rows). A
// bundle with children lacking a cost is "partial": its total and every % share cover only the
// costed children, and the missing SKUs are listed.

export type BundleCogs = {
    total: number | null;
    partial: boolean;
    missingSkus: string[];
    costedCount: number;
    componentCount: number;
    costBasis?: 'pre_gst_live' | 'post_gst_live' | 'post_gst_snapshot' | 'post_gst_snapshot_earliest';
    asOf?: string | null;
    frozen?: boolean;
} | undefined;

export const formatInr = (n: number | null | undefined): string => {
    if (n === null || n === undefined || isNaN(n as number)) return '—';
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Frozen/as-of COGS carry an `asOf` date (the moment the cost was sealed). Live rows don't.
// Format to a compact "Mon YYYY" (e.g. "Jun 2026"); guard against null/undefined/unparseable.
export const formatAsOfMonth = (asOf: string | null | undefined): string | null => {
    if (!asOf) return null;
    const d = new Date(asOf);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

export const missingSkusLabel = (skus: string[]): string =>
    skus.length <= 8 ? skus.join(', ') : `${skus.slice(0, 8).join(', ')} +${skus.length - 8} more`;
